import type { FillAction, ActionResult } from '../types/actions';
import { waitForElement, scrollIntoView, isElementVisible, isElementEnabled } from '../utils/dom-utils';

export async function executeFillAction(action: FillAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // Find element
    let element = document.querySelector(action.selector);

    // Wait for element if specified
    if (!element && action.waitForElement) {
      element = await waitForElement(action.selector, action.waitForElement);
    }

    if (!element) {
      return {
        success: false,
        action,
        error: `Element not found: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: false
      };
    }

    // Check if element is a fillable element
    if (!(element instanceof HTMLInputElement ||
          element instanceof HTMLTextAreaElement ||
          element instanceof HTMLSelectElement)) {
      return {
        success: false,
        action,
        error: `Element is not fillable: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Check if element is visible
    if (!isElementVisible(element)) {
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      const reasons = [];

      if (style.display === 'none') reasons.push('display: none');
      if (style.visibility === 'hidden') reasons.push('visibility: hidden');
      if (style.opacity === '0') reasons.push('opacity: 0');
      if (rect.width === 0 || rect.height === 0) reasons.push(`no dimensions (${rect.width}x${rect.height})`);

      return {
        success: false,
        action,
        error: `Element not visible: ${action.selector}. Reason: ${reasons.join(', ') || 'hidden by parent or CSS'}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Check if element is enabled
    if (!isElementEnabled(element)) {
      return {
        success: false,
        action,
        error: `Element is disabled: ${action.selector}. Element has disabled='true' attribute.`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Scroll into view if specified
    if (action.scrollIntoView !== false) {
      scrollIntoView(element);
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Save currently focused element so we can restore it after filling
    const previousActiveElement = document.activeElement as HTMLElement | null;

    // Focus the element (needed to trigger framework handlers)
    element.focus({ preventScroll: true });

    const valueToSet = String(action.value);

    // Handle different element types
    if (element instanceof HTMLSelectElement) {
      // Special handling for select elements

      // Clear existing selection if specified
      if (action.clear !== false) {
        element.selectedIndex = -1;
      }

      const options = Array.from(element.options);
      const norm = (s: string) => s.trim().toLowerCase();
      const target = norm(valueToSet);
      // Real options, ignoring an empty-value placeholder ("Select a purpose").
      const realOptions = options.filter((o) => o.value !== '');

      // Bigram Dice similarity for the fuzzy fallback (0..1).
      const bigrams = (s: string) => {
        const map = new Map<string, number>();
        for (let i = 0; i < s.length - 1; i++) {
          const g = s.slice(i, i + 2);
          map.set(g, (map.get(g) ?? 0) + 1);
        }
        return map;
      };
      const similarity = (a: string, b: string) => {
        if (!a || !b) return 0;
        if (a === b) return 1;
        const A = bigrams(a);
        const B = bigrams(b);
        let inter = 0;
        for (const [g, n] of A) inter += Math.min(n, B.get(g) ?? 0);
        const total = a.length - 1 + (b.length - 1);
        return total > 0 ? (2 * inter) / total : 0;
      };

      let matchIndex = -1;
      let inexact = false;

      // 1) Exact match on value or visible text (case-insensitive, trimmed).
      matchIndex = options.findIndex(
        (o) => norm(o.value) === target || norm(o.text) === target
      );

      // 2) Numeric index.
      if (matchIndex === -1) {
        const n = parseInt(valueToSet, 10);
        if (!isNaN(n) && n >= 0 && n < options.length) matchIndex = n;
      }

      // 3) Substring match on value or label, either direction.
      if (matchIndex === -1 && target) {
        matchIndex = options.findIndex((o) => {
          if (o.value === '') return false;
          const v = norm(o.value);
          const t = norm(o.text);
          return (
            v.includes(target) ||
            t.includes(target) ||
            target.includes(v) ||
            target.includes(t)
          );
        });
        if (matchIndex !== -1) inexact = true;
      }

      // 4) Fuzzy fallback: the agent asked for a value that isn't an option
      // (e.g. "Personal" on a purpose select). Pick the closest real option;
      // if nothing is remotely similar, take the first real option so a
      // "randomly fill the form" flow still lands on a valid choice.
      if (matchIndex === -1 && realOptions.length) {
        let best = realOptions[0]!;
        let bestScore = -1;
        for (const o of realOptions) {
          const score = Math.max(
            similarity(target, norm(o.value)),
            similarity(target, norm(o.text))
          );
          if (score > bestScore) {
            bestScore = score;
            best = o;
          }
        }
        matchIndex = options.indexOf(best);
        inexact = true;
      }

      if (matchIndex === -1) {
        return {
          success: false,
          action,
          error: `Option not found in select: "${valueToSet}". Available options: ${options.map((o) => `"${o.value}" (${o.text})`).join(', ')}`,
          timestamp: Date.now(),
          duration: Date.now() - startTime,
          elementFound: true
        };
      }

      const chosen = options[matchIndex]!;
      if (inexact) {
        console.warn(
          `[eazy-navigator] fill select: "${valueToSet}" is not an exact option; using closest match "${chosen.value}" (${chosen.text}).`
        );
      }

      element.selectedIndex = matchIndex;
      chosen.selected = true;
      // Set value property as well
      element.value = chosen.value;

    } else if (element instanceof HTMLInputElement && element.type === 'checkbox') {
      // Special handling for checkbox elements
      const shouldCheck = valueToSet.toLowerCase() === 'true' ||
                         valueToSet === '1' ||
                         valueToSet.toLowerCase() === 'yes' ||
                         valueToSet.toLowerCase() === 'on' ||
                         valueToSet.toLowerCase() === 'checked';

      element.checked = shouldCheck;

    } else if (element instanceof HTMLInputElement && element.type === 'radio') {
      // Special handling for radio button elements
      const shouldCheck = valueToSet.toLowerCase() === 'true' ||
                         valueToSet === '1' ||
                         valueToSet.toLowerCase() === 'yes' ||
                         valueToSet.toLowerCase() === 'on' ||
                         valueToSet.toLowerCase() === 'checked';

      if (shouldCheck) {
        element.checked = true;
      }

    } else if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      // Handle regular input and textarea elements

      // Clear existing value if specified
      if (action.clear !== false) {
        element.value = '';
      }

      // Set the value
      element.value = valueToSet;
    }

    // Trigger input event
    const inputEvent = new Event('input', {
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(inputEvent);

    // Trigger change event (important for select elements)
    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(changeEvent);

    // Restore focus to EVA widget input (prevents stealing focus during form filling)
    element.blur();
    const evaInput = document.querySelector('.eva-input') as HTMLElement | null;
    if (evaInput) {
      try { evaInput.focus({ preventScroll: true }); } catch (_e) { /* ignore */ }
    } else if (previousActiveElement && previousActiveElement !== document.body && previousActiveElement !== element) {
      try { previousActiveElement.focus({ preventScroll: true }); } catch (_e) { /* ignore */ }
    }

    // Get validation state
    const isValid = element.checkValidity ? element.checkValidity() : true;
    const validationMessage = element.validationMessage || undefined;

    return {
      success: true,
      action,
      timestamp: Date.now(),
      duration: Date.now() - startTime,
      elementFound: true,
      data: {
        fieldType: element.tagName.toLowerCase(),
        inputType: element instanceof HTMLInputElement ? element.type : undefined,
        newValue: element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement
          ? element.value
          : action.value,
        isValid,
        validationMessage
      }
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}
